/**
 * Repositorio generico — o unico lugar por onde os dados sao escritos.
 *
 * Concentrar aqui todas as escritas e o que torna possivel garantir, de uma so
 * vez e para todos os plugins presentes e futuros:
 *   - regra 4: nada e escrito sem passar por um `Validator` e sem gerar `AuditEntry`;
 *   - regra 6: nao existe apagar fisico — `remove()` marca `deletedAt`;
 *   - regra 9: escreve-se no IndexedDB e devolve-se ja; a fila (`_outbox`) trata
 *     do resto quando houver rede.
 *
 * Registro + auditoria + fila vao na MESMA transacao Dexie. Se qualquer um falhar,
 * nao fica nem meia escrita: uma entrada de auditoria sem registro seria uma
 * mentira no historico, e um item de fila sem registro enviaria lixo ao servidor.
 *
 * E aqui que as permissoes sao aplicadas de verdade. Filtrar rotas e botoes pelo
 * escopo e cortesia com quem usa o aplicativo; nao e seguranca. Enquanto a
 * verificacao nao existiu neste arquivo, o modo leitura-apenas era decorativo:
 * as permissoes eram calculadas certas, passadas ao nucleo, e depois ninguem lhes
 * perguntava nada. Qualquer `repository().create()` escrevia na mesma.
 */
import {
  CoreEvents,
  PermissionDeniedError,
  isDeleted,
  issuesToText,
  ulid,
  type AuditActor,
  type AuditOperation,
  type BaseRecord,
  type Logger,
  type OutboxItem,
  type RecordChangedPayload,
  type RepositoryLike,
  type Scope,
  type ScopeAction,
  type ValidationIssue,
  type Validator,
} from '@flos/shared';
import type { AuditRow, FlosDatabase } from './db';
import type { EventBus } from './events';
import type { SessionService } from './session';

/** Campos que o chamador nunca define a mao: sao do repositorio. */
const PROTECTED_FIELDS = new Set<string>([
  'id',
  'familyId',
  'createdAt',
  'createdBy',
  'updatedAt',
  'updatedBy',
  'deletedAt',
  'deletedBy',
  'version',
  'syncState',
]);

/** Chaves de `list()` que sao opcoes, nao filtros de igualdade. */
const LIST_OPTION_KEYS = new Set(['includeDeleted', 'limit', 'offset', 'orderBy', 'direction']);

export interface WriteOptions {
  /** Origem da escrita. Distinguir a IA do humano e o que torna a IA auditavel. */
  actor?: AuditActor;
  /** Quem assume a responsabilidade. Por padrão, o membro da sessao. */
  memberId?: string;
  /** Interpretacao da IA que originou esta escrita. */
  aiTraceId?: string;
  /** Regra de automacao que disparou esta escrita. */
  automationRuleId?: string;
  /** Concorrencia otimista: recusa se a versao em disco for outra. */
  expectedVersion?: number;
}

export interface ListQuery extends Record<string, unknown> {
  /** Por padrão os apagados nao aparecem. */
  includeDeleted?: boolean;
  limit?: number;
  offset?: number;
  orderBy?: string;
  direction?: 'asc' | 'desc';
}

export class ValidationError extends Error {
  readonly code = 'VALIDATION_FAILED';
  constructor(
    readonly entity: string,
    readonly issues: ValidationIssue[],
  ) {
    super(issuesToText(issues) || 'Dados invalidos.');
    this.name = 'ValidationError';
  }
}

export class RecordNotFoundError extends Error {
  readonly code = 'RECORD_NOT_FOUND';
  constructor(
    readonly entity: string,
    readonly recordId: string,
  ) {
    super('Nao encontrei esse registro. Pode ter sido apagado noutro dispositivo.');
    this.name = 'RecordNotFoundError';
  }
}

export class RecordDeletedError extends Error {
  readonly code = 'RECORD_DELETED';
  constructor(
    readonly entity: string,
    readonly recordId: string,
  ) {
    super('Esse registro esta apagado. Restaure-o antes de o alterar.');
    this.name = 'RecordDeletedError';
  }
}

export class VersionConflictError extends Error {
  readonly code = 'VERSION_CONFLICT';
  constructor(
    readonly entity: string,
    readonly recordId: string,
    readonly expected: number,
    readonly atual: number,
  ) {
    super('Este registro foi alterado entretanto. Volte a abrir para ver a versao mais recente.');
    this.name = 'VersionConflictError';
  }
}

/**
 * Validadores por entidade. Fica fora do `EntityDescriptor` porque o manifesto
 * tem de ser legivel sem carregar o plugin (regra 2) e um validador e codigo.
 */
export class ValidatorRegistry {
  readonly #validators = new Map<string, Validator<unknown>>();

  register(entity: string, validator: Validator<unknown>): void {
    this.#validators.set(entity, validator);
  }

  get(entity: string): Validator<unknown> | undefined {
    return this.#validators.get(entity);
  }

  has(entity: string): boolean {
    return this.#validators.has(entity);
  }
}

export interface RepositoryDeps {
  db: FlosDatabase;
  events: EventBus;
  logger: Logger;
  session: SessionService;
  validators: ValidatorRegistry;
  /** Injetavel para tornar os testes deterministas. */
  now?: () => string;
}

export class Repository<T extends BaseRecord> implements RepositoryLike<T> {
  readonly entity: string;
  readonly #deps: RepositoryDeps;
  readonly #defaults: WriteOptions;

  constructor(entity: string, deps: RepositoryDeps, defaults: WriteOptions = {}) {
    this.entity = entity;
    this.#deps = deps;
    this.#defaults = defaults;
  }

  /** Copia deste repositorio com outra origem — ex. escritas confirmadas da IA. */
  as(defaults: WriteOptions): Repository<T> {
    return new Repository<T>(this.entity, this.#deps, { ...this.#defaults, ...defaults });
  }

  // --- Leitura ---

  async get(id: string): Promise<T | null> {
    this.#assertCan('read');
    const familyId = this.#deps.session.familyId;
    const record = await this.#table().get(id);
    // Fronteira de isolamento: um id de outra familia responde como inexistente.
    if (!record || record.familyId !== familyId) return null;
    return record;
  }

  /**
   * Listagem sempre restrita a familia ativa. `includeDeleted`, `limit`,
   * `offset`, `orderBy` e `direction` sao opcoes; as restantes chaves sao
   * filtros de igualdade.
   */
  async list(filter: ListQuery = {}): Promise<T[]> {
    this.#assertCan('read');
    const familyId = this.#deps.session.familyId;
    const includeDeleted = filter.includeDeleted === true;
    const equality = Object.entries(filter).filter(([key]) => !LIST_OPTION_KEYS.has(key));

    let rows = await this.#table().where('familyId').equals(familyId).toArray();

    // Cinto e suspensorios: o indice ja restringe, mas esta e a fronteira de
    // privacidade do aplicativo inteiro e nao depende de um indice estar correto.
    rows = rows.filter((row) => row.familyId === familyId);

    if (!includeDeleted) rows = rows.filter((row) => !isDeleted(row));

    if (equality.length > 0) {
      rows = rows.filter((row) =>
        equality.every(([key, value]) => (row as Record<string, unknown>)[key] === value),
      );
    }

    const orderBy = typeof filter.orderBy === 'string' ? filter.orderBy : null;
    if (orderBy) {
      const factor = filter.direction === 'desc' ? -1 : 1;
      rows = [...rows].sort((a, b) => factor * compareValues(
        (a as Record<string, unknown>)[orderBy],
        (b as Record<string, unknown>)[orderBy],
      ));
    }

    const offset = typeof filter.offset === 'number' && filter.offset > 0 ? filter.offset : 0;
    if (offset > 0) rows = rows.slice(offset);
    if (typeof filter.limit === 'number' && filter.limit >= 0) rows = rows.slice(0, filter.limit);
    return rows;
  }

  async count(filter: ListQuery = {}): Promise<number> {
    return (await this.list(filter)).length;
  }

  // --- Escrita ---

  async create(input: Partial<T>, options: WriteOptions = {}): Promise<T> {
    this.#assertCan('write');
    const opts = { ...this.#defaults, ...options };
    const session = this.#deps.session.current();
    const at = this.#now();
    const memberId = opts.memberId ?? session.memberId;

    const draft = stripProtected(input);
    const record = {
      ...draft,
      id: typeof input.id === 'string' && input.id ? input.id : ulid(),
      familyId: session.familyId,
      createdAt: at,
      createdBy: memberId,
      updatedAt: at,
      updatedBy: memberId,
      deletedAt: null,
      deletedBy: null,
      version: 1,
      syncState: 'local',
    } as unknown as T;

    await this.#transaction(async () => {
      this.#validate(record);
      // `add` e nao `put`: um id repetido e um erro, nao uma substituicao silenciosa.
      await this.#table().add(record);
      await this.#writeAudit({
        operation: 'create',
        record,
        before: null,
        after: record,
        at,
        memberId,
        opts,
      });
      await this.#enqueue('create', record, 0, at);
    });

    this.#announce('create', record, opts);
    return record;
  }

  async update(id: string, patch: Partial<T>, options: WriteOptions = {}): Promise<T> {
    this.#assertCan('write');
    const opts = { ...this.#defaults, ...options };
    const session = this.#deps.session.current();
    const at = this.#now();
    const memberId = opts.memberId ?? session.memberId;
    let next!: T;

    await this.#transaction(async () => {
      const current = await this.#require(id, session.familyId);
      if (isDeleted(current)) throw new RecordDeletedError(this.entity, id);
      this.#assertVersion(current, opts, id);

      next = {
        ...current,
        ...stripProtected(patch),
        updatedAt: at,
        updatedBy: memberId,
        version: current.version + 1,
        syncState: 'local',
      } as T;

      this.#validate(next);
      await this.#table().put(next);
      await this.#writeAudit({
        operation: 'update',
        record: next,
        before: current,
        after: next,
        at,
        memberId,
        opts,
      });
      await this.#enqueue('update', next, current.version, at);
    });

    this.#announce('update', next, opts);
    return next;
  }

  /**
   * Apagar e marcar. Nunca `table.delete()` — o expurgo fisico e uma rotina
   * separada e explicita (regra 6).
   */
  async remove(id: string, options: WriteOptions = {}): Promise<void> {
    await this.softDelete(id, options);
  }

  /** Igual a `remove()`, mas devolve o registro apagado (util para o "desfazer"). */
  async softDelete(id: string, options: WriteOptions = {}): Promise<T> {
    this.#assertCan('delete');
    const opts = { ...this.#defaults, ...options };
    const session = this.#deps.session.current();
    const at = this.#now();
    const memberId = opts.memberId ?? session.memberId;
    let next!: T;
    let changed = true;

    await this.#transaction(async () => {
      const current = await this.#require(id, session.familyId);
      if (isDeleted(current)) {
        // Apagar duas vezes nao e erro do usuário; devolvemos o estado atual
        // sem gerar auditoria nem evento para uma coisa que nao aconteceu.
        next = current;
        changed = false;
        return;
      }
      this.#assertVersion(current, opts, id);

      next = {
        ...current,
        deletedAt: at,
        deletedBy: memberId,
        updatedAt: at,
        updatedBy: memberId,
        version: current.version + 1,
        syncState: 'local',
      } as T;

      await this.#table().put(next);
      await this.#writeAudit({
        operation: 'delete',
        record: next,
        before: current,
        after: next,
        at,
        memberId,
        opts,
      });
      await this.#enqueue('delete', next, current.version, at);
    });

    if (changed) this.#announce('delete', next, opts);
    return next;
  }

  async restore(id: string, options: WriteOptions = {}): Promise<T> {
    // Restaurar repoe dados, nao os destroi: pede `write`, nao `delete`. Quem
    // perdeu a permissao de apagar continua a poder desfazer o que apagou.
    this.#assertCan('write');
    const opts = { ...this.#defaults, ...options };
    const session = this.#deps.session.current();
    const at = this.#now();
    const memberId = opts.memberId ?? session.memberId;
    let next!: T;
    let changed = true;

    await this.#transaction(async () => {
      const current = await this.#require(id, session.familyId);
      if (!isDeleted(current)) {
        next = current;
        changed = false;
        return;
      }
      this.#assertVersion(current, opts, id);

      next = {
        ...current,
        deletedAt: null,
        deletedBy: null,
        updatedAt: at,
        updatedBy: memberId,
        version: current.version + 1,
        syncState: 'local',
      } as T;

      await this.#table().put(next);
      await this.#writeAudit({
        operation: 'restore',
        record: next,
        before: current,
        after: next,
        at,
        memberId,
        opts,
      });
      await this.#enqueue('restore', next, current.version, at);
    });

    if (changed) this.#announce('restore', next, opts);
    return next;
  }

  /**
   * Desfaz uma operacao a partir da sua entrada de auditoria.
   *
   * Reverter e, ele proprio, uma escrita auditada: o historico cresce, nunca
   * encolhe. Reverter um `create` apaga (soft); reverter um `delete` restaura;
   * tudo o resto repoe o estado `before`.
   */
  async revert(auditEntryId: string, options: WriteOptions = {}): Promise<T> {
    this.#assertCan('write');
    const opts = { ...this.#defaults, ...options };
    const session = this.#deps.session.current();
    const at = this.#now();
    const memberId = opts.memberId ?? session.memberId;
    let next!: T;
    let operation: AuditOperation = 'update';

    await this.#transaction(async () => {
      const entry = await this.#deps.db.audit.get(auditEntryId);
      if (!entry || entry.familyId !== session.familyId) {
        throw new RecordNotFoundError(this.entity, auditEntryId);
      }
      if (entry.entity !== this.entity) {
        throw new Error(
          `A entrada de auditoria pertence a "${entry.entity}" e nao a "${this.entity}".`,
        );
      }

      const current = await this.#require(entry.recordId, session.familyId);
      const before = entry.before as T | null;

      if (before == null) {
        // Reverter uma criacao = apagar (soft). O registro continua no historico.
        // O escopo exigido segue o que a reversao FAZ, nao o que ela se chama:
        // desfazer uma criacao apaga, e apagar exige `delete`.
        this.#assertCan('delete');
        operation = 'delete';
        next = {
          ...current,
          deletedAt: at,
          deletedBy: memberId,
          updatedAt: at,
          updatedBy: memberId,
          version: current.version + 1,
          syncState: 'local',
        } as T;
      } else {
        operation = isDeleted(current) && !isDeleted(before) ? 'restore' : 'update';
        next = {
          ...before,
          // A identidade e a origem nunca mudam, mesmo a reverter.
          id: current.id,
          familyId: current.familyId,
          createdAt: current.createdAt,
          createdBy: current.createdBy,
          updatedAt: at,
          updatedBy: memberId,
          version: current.version + 1,
          syncState: 'local',
        } as T;
      }

      // O estado anterior ja foi aceite uma vez; se as regras de validacao
      // mudaram entretanto, avisamos mas nao bloqueamos — reverter tem de
      // funcionar sempre (regra 6), senao o usuário fica preso ao erro.
      const issues = this.#issues(next);
      if (issues) {
        this.#deps.logger.warn('Reversao repoe um estado que ja nao valida', {
          entity: this.entity,
          recordId: next.id,
          problemas: issues.length,
        });
      }

      await this.#table().put(next);
      await this.#writeAudit({
        operation,
        record: next,
        before: current,
        after: next,
        at,
        memberId,
        opts,
        revertedFrom: entry.id,
      });
      await this.#enqueue(operation, next, current.version, at);
    });

    this.#announce(operation, next, opts);
    return next;
  }

  // --- Interno ---

  /**
   * Escopo exigido por uma operacao nesta entidade.
   *
   * As entidades chamam-se `<plugin>.<nome>` e os escopos `<plugin>:<acao>`.
   * E essa correspondencia — garantida pelo teste de arquitectura da regra 2 —
   * que deixa o nucleo aplicar permissoes sem conhecer dominio nenhum (regra 1).
   */
  #scope(action: ScopeAction): Scope {
    const dot = this.entity.indexOf('.');
    const plugin = dot === -1 ? this.entity : this.entity.slice(0, dot);
    return `${plugin}:${action}`;
  }

  #assertCan(action: ScopeAction): void {
    // Sem sessao nenhuma o problema nao e de permissao, e de arranque: deixamos
    // `current()` lancar `NoSessionError`, que diz a coisa certa a quem depura.
    this.#deps.session.current();
    const scope = this.#scope(action);
    if (this.#deps.session.can(scope)) return;
    throw new PermissionDeniedError(scope, this.#deps.session.permissions.role);
  }

  #table() {
    return this.#deps.db.records<T>(this.entity);
  }

  #now(): string {
    return this.#deps.now ? this.#deps.now() : new Date().toISOString();
  }

  async #transaction(work: () => Promise<void>): Promise<void> {
    const db = this.#deps.db;
    await db.transaction('rw', [this.#table(), db.audit, db.outbox], work);
  }

  async #require(id: string, familyId: string): Promise<T> {
    const record = await this.#table().get(id);
    if (!record || record.familyId !== familyId) {
      throw new RecordNotFoundError(this.entity, id);
    }
    return record;
  }

  #assertVersion(current: T, opts: WriteOptions, id: string): void {
    if (opts.expectedVersion != null && opts.expectedVersion !== current.version) {
      throw new VersionConflictError(this.entity, id, opts.expectedVersion, current.version);
    }
  }

  #issues(record: T): ValidationIssue[] | null {
    const validator = this.#deps.validators.get(this.entity);
    if (!validator) return null;
    const result = validator(record);
    return result.ok ? null : result.issues;
  }

  #validate(record: T): void {
    const issues = this.#issues(record);
    if (issues) throw new ValidationError(this.entity, issues);
  }

  async #writeAudit(input: {
    operation: AuditOperation;
    record: T;
    before: unknown;
    after: unknown;
    at: string;
    memberId: string;
    opts: WriteOptions;
    revertedFrom?: string;
  }): Promise<void> {
    const row: AuditRow = {
      id: ulid(),
      familyId: input.record.familyId,
      entity: this.entity,
      recordId: input.record.id,
      operation: input.operation,
      actor: input.opts.actor ?? 'user',
      memberId: input.memberId,
      at: input.at,
      before: input.before ?? null,
      after: input.after ?? null,
    };
    if (input.opts.aiTraceId) row.aiTraceId = input.opts.aiTraceId;
    if (input.opts.automationRuleId) row.automationRuleId = input.opts.automationRuleId;
    if (input.revertedFrom) row.revertedFrom = input.revertedFrom;
    await this.#deps.db.audit.add(row);
  }

  async #enqueue(
    operation: OutboxItem['operation'],
    record: T,
    baseVersion: number,
    at: string,
  ): Promise<void> {
    const item: OutboxItem = {
      id: ulid(),
      entity: this.entity,
      recordId: record.id,
      operation,
      payload: record,
      baseVersion,
      queuedAt: at,
      attempts: 0,
    };
    await this.#deps.db.outbox.add(item);
  }

  /** Emitido so depois de a transacao fechar: ninguem reage a algo que abortou. */
  #announce(operation: AuditOperation, record: T, opts: WriteOptions): void {
    const payload: RecordChangedPayload = {
      entity: this.entity,
      recordId: record.id,
      operation,
      source: toEventSource(opts.actor ?? 'user'),
      record,
    };
    this.#deps.events.emit(CoreEvents.RECORD_CHANGED, payload);
  }
}

/** Fabrica de repositorios ligados a sessao ativa. */
export class RepositoryFactory {
  readonly #deps: RepositoryDeps;
  readonly #cache = new Map<string, Repository<BaseRecord>>();

  constructor(deps: RepositoryDeps) {
    this.#deps = deps;
  }

  for<T extends BaseRecord>(entity: string): Repository<T> {
    const cached = this.#cache.get(entity);
    if (cached) return cached as unknown as Repository<T>;
    const repo = new Repository<T>(entity, this.#deps);
    this.#cache.set(entity, repo as unknown as Repository<BaseRecord>);
    return repo;
  }

  get validators(): ValidatorRegistry {
    return this.#deps.validators;
  }
}

/** `RecordChangedPayload.source` nao conhece 'system'; tratamo-lo como automacao. */
function toEventSource(actor: AuditActor): RecordChangedPayload['source'] {
  return actor === 'system' ? 'automation' : actor;
}

function stripProtected<T>(input: Partial<T>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(input as Record<string, unknown>)) {
    if (PROTECTED_FIELDS.has(key)) continue;
    if (value === undefined) continue;
    out[key] = value;
  }
  return out;
}

function compareValues(a: unknown, b: unknown): number {
  if (a === b) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), 'pt');
}
